/** Section 6 — "Proposed Format for HOLOL" (EN PDF). */
export const programFormatEn = {
  id: 'program-format',
  navLabel: 'Program format',
  eyebrow: 'Program format',
  title: 'Proposed Format for “HOLOL”',
  subtitle:
    "A weekly 60-minute economic and investment program presenting a country's vision, economy, and opportunities across ten connected segments.",
  blocks: [
    {
      icon: 'badge',
      title: 'Program Name',
      paragraphs: [
        'HOLOL — an international economic and investment program.',
        'The local market name may be adapted while maintaining the unified identity of the program, for example:',
      ],
      chips: ['HOLOL Egypt', 'HOLOL Saudi Arabia', 'HOLOL UAE', 'HOLOL UK', 'HOLOL Singapore'],
    },
    {
      icon: 'live_tv',
      title: 'Program Nature',
      paragraphs: [
        "A weekly economic and investment program with a 60-minute episode, including commercial breaks. Each episode presents the country, its economy, and its opportunities through a live television format combining:",
      ],
      flow: [
        'Government officials',
        'Investors',
        'Companies',
        'Financial institutions',
        'Experts',
        'Economic audience',
        'International guests',
      ],
      flowJoin: '+',
      stats: [
        { value: '60', label: 'Minutes per episode' },
        { value: '52', label: 'Episodes per year' },
        { value: '1 → 2', label: 'Weekly episodes as the project expands' },
      ],
      closing: [
        'The episode is not built around one long interview. Instead, it consists of connected segments built around one main economic or investment file.',
        'Proposed frequency: one episode per week in the first phase — 52 episodes annually. As the project expands, the format can move to two weekly episodes.',
      ],
    },
    {
      icon: 'theaters',
      title: 'Studio Design',
      wide: true,
      paragraphs: [
        'The program takes place in a large economic television studio or stage, rather than a conventional interview studio. It includes:',
      ],
      labeled: [
        { label: 'Main stage', text: 'Host and principal guests.' },
        {
          label: 'Data screen',
          text: 'Maps, indicators, projects, charts, images, and visual presentations.',
        },
        {
          label: 'Audience area',
          text: 'Business leaders, investors, financial institutions, companies, and experts.',
        },
        {
          label: 'Opportunity room',
          text: 'A separate or semi-separate area for focused and limited meetings.',
        },
      ],
      closing:
        'The studio is designed so that movement between these areas creates visual and rhythmic changes throughout the episode.',
    },
    {
      type: 'segments',
      icon: 'view_agenda',
      title: 'Episode Structure',
      items: [
        {
          tag: 'Segment One',
          name: '“The Issue”',
          duration: '3 min',
          points: [
            'A fast visual opening introduces the country, sector, or project at the center of the episode.',
            'The episode title appears, followed by the central question: What are we trying to understand or achieve today?',
          ],
        },
        {
          tag: 'Segment Two',
          name: '“The Country’s Vision”',
          duration: '7 min',
          points: [
            'A senior official or principal figure presents the vision, objectives, priorities, projects, needs, and opportunities.',
            'The conversation is focused and direct rather than a conventional press interview.',
          ],
        },
        {
          tag: 'Segment Three',
          name: '“The Market in Numbers”',
          duration: '5 min',
          points: [
            'A visual presentation of key data: market size, growth, investment, sectors, opportunities, and challenges.',
            'Maps, motion graphics, and artificial intelligence can be used to present the information.',
          ],
        },
        {
          tag: 'Segment Four',
          name: '“The Opportunity”',
          duration: '6 min',
          points: [
            'The opportunity is defined in practical terms. It may be a project, sector, investment, financing requirement, technology, partnership, asset, or auction.',
            'The question is: What is the opportunity, and what does it need in order to move forward?',
          ],
        },
        {
          tag: 'Segment Five',
          name: '“Who Can Participate?”',
          duration: '7 min',
          points: [
            'A party from outside the local market joins: an investor, bank, fund, company, institution, or strategic partner.',
            'The central question becomes: Does this opportunity fit you, and what would you need to participate?',
          ],
        },
        { isBreak: true, name: 'Commercial Break', duration: '3 min' },
        {
          tag: 'Segment Six',
          name: '“The International Dialogue”',
          duration: '7 min',
          points: [
            'A live connection or international guest joins the program.',
            'The local and international sides discuss the possibility of cooperation.',
          ],
        },
        {
          tag: 'Segment Seven',
          name: '“The Market Council”',
          duration: '6 min',
          points: [
            'A selected group of business leaders, investors, and experts joins the discussion.',
            'The issue is examined from several perspectives, giving the program the character of a live economic council.',
          ],
        },
        {
          tag: 'Segment Eight',
          name: '“The Opportunity Room”',
          duration: '7 min',
          points: [
            'A limited group moves into a more focused session — for example: official + investor + bank + specialized company.',
            'The discussion focuses on: What is needed? Who can provide it? What are the obstacles? What is the next step?',
            'The meeting may continue beyond the broadcast depending on the nature of the file.',
          ],
        },
        {
          tag: 'Segment Nine',
          name: '“The Global Bridge”',
          duration: '4 min',
          points: [
            'A short connection with another market: How does the international market see this opportunity?',
            'This demonstrates the direct relationship between “HOLOL” and other markets.',
          ],
        },
        {
          tag: 'Segment Ten',
          name: '“What Happens Next?”',
          duration: '2 min',
          points: [
            'The host returns to the main stage and summarizes: What did we learn? What is the opportunity? Who responded to it? What is the next step?',
          ],
        },
      ],
      signatureLabel: 'The episode closes with a consistent signature line:',
      signature: 'From Vision to Opportunity… From Opportunity to Market.',
    },
    {
      type: 'table',
      icon: 'schedule',
      title: 'Episode Timing',
      headers: ['Segment', 'Duration'],
      rows: [
        ['The Issue', '3 minutes'],
        ["The Country's Vision", '7 minutes'],
        ['The Market in Numbers', '5 minutes'],
        ['The Opportunity', '6 minutes'],
        ['Who Can Participate?', '7 minutes'],
        ['Commercial Break', '3 minutes'],
        ['The International Dialogue', '7 minutes'],
        ['The Market Council', '6 minutes'],
        ['The Opportunity Room', '7 minutes'],
        ['The Global Bridge', '4 minutes'],
        ['What Happens Next?', '2 minutes'],
      ],
      footer: { label: 'Total', value: '57 minutes' },
      notes: [
        'The remaining 3 minutes provide flexibility for the opening titles, transitions, or additional time for selected segments, bringing the total episode duration to 60 minutes.',
      ],
    },
    {
      icon: 'dashboard_customize',
      title: 'Episode Types',
      paragraphs: ['While the format remains consistent, the nature of the episode can change:'],
      labeled: [
        {
          label: 'Country episode',
          text: 'The vision, opportunities, and target markets of a country.',
        },
        {
          label: 'Sector episode',
          text: 'Real estate, energy, industry, technology, tourism, and other sectors.',
        },
        {
          label: 'Project episode',
          text: 'A major project seeking investors, partners, or financing.',
        },
        {
          label: 'International opportunity episode',
          text: 'A local opportunity presented to external markets.',
        },
        {
          label: 'HOLOL partnerships episode',
          text: 'A challenge or need and the search for parties capable of addressing it.',
        },
        {
          label: 'Follow-up episode',
          text: 'A relationship or opportunity that began in an earlier episode and has developed further.',
        },
      ],
    },
    {
      icon: 'smart_toy',
      title: 'Technology',
      paragraphs: [
        'Artificial intelligence and visual technologies can be used for:',
      ],
      chips: [
        'Economic maps',
        'Data analysis',
        'Interactive graphics',
        'Market comparisons',
        'Project presentations',
        'Visual simulations',
        'Direct connections between countries',
      ],
      closing:
        'Technology therefore becomes part of the storytelling rather than merely a visual attraction.',
    },
    {
      icon: 'emoji_events',
      title: 'Result',
      wide: true,
      highlight: true,
      paragraphs: ['The proposed format allows every episode to produce, simultaneously:'],
      flow: [
        'A television program',
        'Digital content',
        'Economic dialogue',
        'New relationships',
        'Opportunities that can be developed',
      ],
      flowJoin: '+',
      closing:
        'The final directing and production approach remains open for refinement with the production company and broadcaster or platform according to technical capabilities and budget, while preserving the core format structure.',
    },
  ],
};
